import React, { useRef } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './Testimonials.css';
import { Card, CardContent } from '@/components/ui/card';
import { Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'Sarah Mitchell',
    role: 'Boutique Owner',
    content: 'Filing Protection\'s team made trademark registration incredibly simple. They guided me through every step and my brand is now fully protected. Highly recommend their services!',
    image: 'https://images.unsplash.com/photo-1580894732444-8ecded7900cd?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop'
  },
  {
    name: 'Michael Chen',
    role: 'Tech Startup Founder',
    content: 'Professional, efficient, and knowledgeable. Filing Protection handled our company formation and trademark registration seamlessly. Their expertise saved us countless hours.',
    image: 'https://images.unsplash.com/photo-1611095790444-1dfa35e37b52?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop'
  },
  {
    name: 'Jennifer Rodriguez',
    role: 'Author',
    content: 'I needed copyright protection for my books and Filing Protection delivered exceptional service. Fast, affordable, and thorough. I now have peace of mind knowing my work is protected.',
    image: 'https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?w=100&h=100&fit=crop'
  },
  {
    name: 'David Thompson',
    role: 'E-commerce Seller',
    content: 'The Amazon Brand Registry service was a game-changer for my business. Filing Protection handled everything professionally and now my products are fully protected from counterfeiters.',
    image: 'https://images.pexels.com/photos/1367269/pexels-photo-1367269.jpeg?w=100&h=100&fit=crop'
  },
  {
    name: 'Lisa Anderson',
    role: 'Restaurant Owner',
    content: 'From trademark search to final registration, Filing Protection provided outstanding service. Their detailed search report gave me confidence before investing in my brand. Worth every penny!',
    image: 'https://images.unsplash.com/photo-1758519288969-4806f015852d?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop'
  },
  {
    name: 'Robert Martinez',
    role: 'Fitness Coach',
    content: 'The entire process was stress-free and transparent. They answered all my questions promptly and kept me updated throughout. My fitness brand is now officially trademarked!',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop'
  },
  {
    name: 'Emily Parker',
    role: 'Fashion Designer',
    content: 'I was worried about trademark costs, but their $45 package was perfect for my startup budget. The process was smooth and my fashion line is now protected. Excellent value!',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop'
  },
  {
    name: 'James Wilson',
    role: 'Software Developer',
    content: 'As a first-time entrepreneur, I had no idea where to start with trademarks. Their team educated me throughout the process and made everything crystal clear. Highly professional!',
    image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop'
  },
  {
    name: 'Maria Garcia',
    role: 'Beauty Brand Owner',
    content: 'Their comprehensive search saved me from a potential lawsuit! They discovered conflicts I would have never found myself. Grateful for their thorough attention to detail.',
    image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop'
  },
  {
    name: 'Christopher Lee',
    role: 'Manufacturing Executive',
    content: 'We\'ve filed multiple trademarks through Filing Protection and the consistency in service quality is remarkable. They\'re now our go-to partner for all IP protection needs.',
    image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop'
  }
];

export const Testimonials = () => {
  const sliderRef = useRef(null);

  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 3, // Default: show 3 on desktop
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    centerMode: true, // Enable centerMode by default for blur effect
    centerPadding: '0px',
    focusOnSelect: true,
    pauseOnHover: false,
    arrows: false,
    responsive: [
      {
        breakpoint: 1024, // Desktop and tablets
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: '0px',
          focusOnSelect: true,
        }
      },
      {
        breakpoint: 768, // Mobile landscape and below
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: false,
          centerPadding: '0px',
          arrows: false,
          focusOnSelect: false,
        }
      },
      {
        breakpoint: 480, // Small mobile
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: false,
          centerPadding: '0px',
          arrows: false,
          focusOnSelect: false,
        }
      }
    ],
  };

  return (
    <section className="py-20 md:py-32 bg-brand-navy-light overflow-hidden testimonials-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-16">
          <p className="text-blue-400 font-semibold mb-2 uppercase tracking-wide">TESTIMONIALS</p>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            What Our Clients Say
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Hear from businesses we've helped protect and grow
          </p>
        </div>

        <div className="overflow-hidden md:overflow-visible">
          <div className="testimonials-slider-wrapper">
            <Slider ref={sliderRef} {...settings}>
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="md:px-3">
                <Card className="h-full border border-blue-900/30 bg-brand-navy-card shadow-lg testimonial-card w-full">
                  <CardContent className="p-6 md:p-8">
                    <Quote className="w-10 h-10 md:w-12 md:h-12 mb-4 text-blue-400 opacity-20" />
                    <p className="text-gray-300 text-sm md:text-base mb-6 leading-relaxed min-h-[100px] md:min-h-[120px]">
                      {testimonial.content}
                    </p>
                    <div className="flex items-center gap-3 md:gap-4">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-white text-sm md:text-base">{testimonial.name}</p>
                        <p className="text-xs md:text-sm text-gray-400">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </Slider>
          </div>
        </div>
      </div>
    </section>
  );
};
